import type { DebouncedFunc } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import { useMemo } from 'react';
import { useCallbackProxy } from './useCallbackProxy';

/**
 * Creates a debounced version of the provided callback function.
 *
 * Unlike throttling, debouncing only executes the callback after the specified
 * wait time has passed since the last invocation. This is ideal for scenarios
 * like text input where you want to wait for the user to stop typing before
 * making an API call.
 *
 * @param callback - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the callback
 */
export const useDebouncedCallback = <T extends (...args: Array<any>) => any>(
  callback: T,
  wait: number
): DebouncedFunc<T> => {
  const callbackProxy = useCallbackProxy(callback);

  const memoizedDebounced = useMemo(
    () =>
      debounce((...args: Array<any>) => callbackProxy(...args), wait, {
        leading: false,
        trailing: true,
      }),
    [callbackProxy, wait]
  );

  return memoizedDebounced;
};
